import React from "react";
import { observer } from "mobx-react-lite";
import Modal from "components/Modal";
import { useTranslation } from "react-i18next";
import ChooseCertSystem from "./features/ChooseCertSystem";
import ConfigureDGNB18 from "./features/ConfigureDGNB18";
import DGNB23AndQNG from "./features/DGNB23AndQNG";
import LifeCycleAssessment from "./features/LifeCycleAssessment";
import Button from "components/Button";
import { certificationExportStore as certExportStore } from "./CertificationExportStore";
import clsx from "clsx";
import { buildingStore } from "store/Building/BuildingStore";
import Checkbox from "components/Checkbox";
import ConfigureBNB from "./features/ConfigureBNB";
import useToast from "hooks/useToast";
import axios from "axios";
import CreateExport from "./features/CreateExport";
import ConfirmModal from "components/ConfirmModal";
import { useNavigate } from "react-router";
import { ROUTES_ENUM } from "routes/consts";
import { IconArrowRight } from "@tabler/icons-react";
import { conformityStore } from "../../store/ConformityStore.ts";

interface MessageObject {
  message: string;
}

const CertificateExporter = observer(
  ({
    open,
    setOpen,
  }: {
    open: boolean;
    setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  }) => {
    const { t } = useTranslation();
    const [loading, setLoading] = React.useState(true);
    const { currentBuilding } = buildingStore;
    const [fileCreated, setFileCreated] = React.useState(false);
    const toast = useToast();
    const navigate = useNavigate();
    const [inProgressMessage, setInProgressMessage] = React.useState("");

    const { activeStep } = certExportStore;

    React.useEffect(() => {
      open && onLoad();
    }, [open]);

    async function onLoad() {
      certExportStore.resetData();
      const inProgressItems =
        await buildingStore.getBuildingExportCertifications("IN_PROGRESS");
      certExportStore.setExportIsInProgress(
        inProgressItems ? inProgressItems?.length > 0 : false
      );
      await conformityStore.fetchCertificationData();
      setLoading(false);
      setInProgressMessage("");
      setFileCreated(false);
    }

    const renderStepContent = () => {
      switch (activeStep) {
        case 0:
          return <ChooseCertSystem />;
        case 1:
          return (
            <LifeCycleAssessment
              loading={loading}
              onClose={() => setOpen(false)}
            />
          );
        case 2:
          return <ConfigureBNB />;
        case 3:
          return <ConfigureDGNB18 />;
        case 4:
          return <DGNB23AndQNG />;
        case 5:
          return <DGNB23AndQNG />;
        case 6:
          return (
            <CreateExport
              onFinish={() => setOpen(false)}
              loading={loading}
              fileCreated={fileCreated}
              inProgressMessage={inProgressMessage}
              navigateToExports={navigateToExports}
            />
          );
        default:
          return null;
      }
    };

    const handleBack = () => {
      certExportStore.setActiveStep(0);
    };

    const handleCreate = async () => {
      if (!currentBuilding) return;
      setLoading(true);
      certExportStore.setActiveStep(6);
      try {
        const response =
          await certExportStore.createCertificationExporterFromApi(
            currentBuilding.id
          );
        if (response?.data.success) {
          setFileCreated(true);
        } else {
          const msg = response?.data.message as MessageObject;
          if (msg) {
            toast(msg.message, "attention");
            setInProgressMessage(msg.message);
          }
        }
      } catch (error) {
        if (axios.isAxiosError(error)) {
          const msg = error?.response?.data.message?.message as string;
          if (msg) {
            setInProgressMessage(msg);
            toast(msg, "attention");
          }
        } else {
          console.error(error);
        }
      } finally {
        setLoading(false);
      }
    };

    function shouldShowSaveChanges() {
      return activeStep != 1 && activeStep != 0 && activeStep != 6;
    }

    function content(): React.ReactNode {
      return (
        <div className={clsx({ "flex flex-col p-6": activeStep !== 1 })}>
          {renderStepContent()}
          {shouldShowSaveChanges() && (
            <div className="border-t border-gray-200">
              <Checkbox
                checked={certExportStore.certExporter.saveSettings}
                labelElement={t("certExport.applyChangesToBuildingSettings")}
                containerClassName="mt-2 !pb-0 mb-0"
                onChange={(e) =>
                  certExportStore.setCertExportFieldAndValue(
                    "saveSettings",
                    e.currentTarget.checked
                  )
                }
              />
            </div>
          )}
        </div>
      );
    }

    function shouldShowBackButton(): boolean {
      if (activeStep === 0 || activeStep === 1 || activeStep === 6)
        return false;
      return true;
    }

    const navigateToExports = () => {
      navigate(ROUTES_ENUM.EXPORTS);
      setOpen(false);
    };

    const getTitle = () => {
      if (activeStep === 0) {
        return t("certExport.title");
      } else if (activeStep > 0 && activeStep < 6) {
        return t("certExport.stepsTitle", {
          certification: certExportStore.certification_key,
        });
      } else {
        return "";
      }
    };

    const isLevelsCert =
      activeStep === 1 && certExportStore.certification_key.includes("Level");

    if (certExportStore.exportIsInProgress && open) {
      return (
        <ConfirmModal
          type="info"
          title={t("certExport.inProgressAlertTitle")}
          confirmButtonTitle={t("certExport.inProgressAlertConfirm")}
          onConfirm={() => setOpen(false)}
          descriptionMesage={t("certExport.inProgressAlertDetails")}
          secondaryButtonTitle={t("certExport.openExports")}
          secondaryButtonAction={navigateToExports}
          secondaryButtonIcon={<IconArrowRight />}
          open={certExportStore.exportIsInProgress}
          close={() => setOpen(false)}
          hideCancel
        />
      );
    }

    return (
      <Modal
        isOpen={open}
        setIsOpen={setOpen}
        closeButton={activeStep !== 6}
        title={getTitle()}
        disableOutsideClick
        containerClassName={clsx(
          "sm:my-8",
          activeStep === 0 ? "min-w-[768px]" : "min-w-[640px]",
          { "!min-w-[500px]": activeStep === 6 }
        )}
        className="!max-h-[calc(100vh-129px)]"
        scrollable
      >
        {loading ? skeleton() : content()}
        {activeStep !== 0 && (
          <div
            className={clsx(
              "bg-gray-50 flex flex-row",
              shouldShowBackButton() ? "justify-between" : "justify-end",
              { "p-4": shouldShowBackButton() || isLevelsCert }
            )}
          >
            {shouldShowBackButton() && (
              <Button type="gray" width="fit-content" onClick={handleBack}>
                {t("DGNB.back")}
              </Button>
            )}
            {(activeStep > 1 || isLevelsCert) && activeStep !== 6 && (
              <Button
                width="fit-content"
                className="px-14"
                type="primary"
                onClick={handleCreate}
              >
                {t("certExport.createExport")}
              </Button>
            )}
          </div>
        )}
      </Modal>
    );
  }
);

export default CertificateExporter;

function skeleton() {
  return (
    <div className="animate-pulse m-6">
      <div className="h-24 w-full bg-gray-200 rounded dark:bg-gray-700"></div>
    </div>
  );
}
